import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { type DeliveryGalleryItem } from '@/services/deliveries';
import { deliveryAltText, deliveryMeta } from '@/utils/deliveryFormat';

import { DeliveryTileBadges } from './DeliveryTileBadges';
import { DeliveryTileMedia } from './DeliveryTileMedia';

type Props = {
  item: DeliveryGalleryItem;
  onOpen: (item: DeliveryGalleryItem) => void;
  sizes: string;
  priority?: boolean;
  /** The grid or the strip decides the tile's shape; the tile never does. */
  sx?: SxProps<Theme>;
};

/**
 * One handover, as a tile. The gallery grid and every proof strip draw this.
 *
 * ## It is a button, not a card with a link in it
 *
 * The whole tile opens a viewer, so the whole tile is one control with one
 * accessible name. The alternative — a div with a click handler, or a card
 * containing a small link — is either unreachable by keyboard or makes a
 * screen-reader user hunt for the one hit target inside something that looked
 * like a single object.
 *
 * The name is the full description of what will open, built by
 * `deliveryAltText`, prefixed with the verb. The picture inside carries
 * `alt=""` so the sentence is read once.
 *
 * ## The caption is always drawn, not revealed on hover
 *
 * A caption that appears on hover does not exist on a phone, and on a wall of
 * other people's cars the caption *is* the proof — the destination and the
 * month are what make it a record rather than a mood board. Hover deepens the
 * scrim and lifts the picture; it does not hand over information that was
 * being withheld.
 */
export function DeliveryTile({ item, onOpen, sizes, priority = false, sx }: Props) {
  const { delivery, media } = item;
  const verb = media.kind === 'video' ? 'Play video' : 'View photograph';

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(item)}
      aria-label={`${verb}: ${deliveryAltText(delivery, media)}${
        delivery.testimonial ? ', with a message from the customer' : ''
      }`}
      sx={[
        (theme) => ({
          position: 'relative',
          display: 'block',
          width: '100%',
          height: '100%',
          p: 0,
          border: 0,
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: 'surfaceVariant',
          // The tile sits on both the default and the muted band, and on a
          // photograph-heavy wall in dark mode an unbordered tile bleeds into
          // the page. A hairline keeps every tile's edge findable in both
          // schemes without drawing a box around the picture.
          boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
          '&:hover img': { transform: 'scale(1.04)' },
          '&:hover .delivery-tile-scrim': { opacity: 1 },
          '&:focus-visible': {
            outline: '3px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <DeliveryTileMedia media={media} sizes={sizes} priority={priority} />

      <DeliveryTileBadges item={item} />

      {/* Aria-hidden: every word of it is already in the button's name. */}
      <Stack
        aria-hidden
        className="delivery-tile-scrim"
        spacing={0.5}
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          px: 3,
          pt: 10,
          pb: 3,
          textAlign: 'left',
          background:
            'linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.42) 52%, rgba(0,0,0,0) 100%)',
          opacity: 0.94,
          transition: 'opacity 260ms ease',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: '#FFFFFF', fontWeight: 700, lineHeight: 1.25 }}
        >
          {delivery.vehicle}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.82)' }}>
          {deliveryMeta(delivery)}
        </Typography>
      </Stack>
    </Box>
  );
}
